import { SIMA, SIMA_TROPHY, STRATA, type StratumId } from '@somnhold/content';
import { hexToWorld, parseHexKey, simaOpen, simaRecord, worldToHex, type CampaignState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/*
 * The Chasm's trophy in the bastion (docs/SIMA.md, «Lo que se ve en el bastión»): Hilde's clan's winch beside the
 * forge. Its look (the group's mark and a stone for each stratum whose guardian fell), where it stands, and the card
 * a touch on it (or on the forge) shows: the mark ever and this week's, when the Chasm remakes itself, who went down
 * each time the mark went deeper (the group's, never a ranking), and what is left to earn.
 */

export interface WinchState {
  look: { mark: number; stones: { stratum: StratumId; floor: number }[] };
  /** Its stage: 0 bare, 1 the second pulley, 2 the banners, 3 the smoke (SIMA_TROPHY). */
  stage: number;
}

/** The floor a stage of the winch comes with. */
export const stageFloor = (stage: number): number => [0, SIMA_TROPHY.pulley, SIMA_TROPHY.banners, SIMA_TROPHY.smoke][stage] ?? 0;

/** The winch once the Chasm is open (null before). */
export const simaWinch = (s: CampaignState): WinchState | null => {
  if (!simaOpen(s)) return null;
  const mark = s.sima?.best ?? 0;
  const stones = (s.sima?.stones ?? []).slice(0, STRATA.length).map((x) => ({ stratum: x.stratum, floor: x.floor }));
  const stage = mark >= SIMA_TROPHY.smoke ? 3 : mark >= SIMA_TROPHY.banners ? 2 : mark >= SIMA_TROPHY.pulley ? 1 : 0;
  return { look: { mark, stones }, stage };
};

/**
 * The cells round the forge the winch prefers (axial offsets): beside it to the right first, then in front (toward the
 * table camera), then the other side and behind. The armory's rack stands front-left of the forge (armero-spot.ts).
 */
const RING: [number, number][] = [[1, 0], [0, 1], [-1, 0], [1, -1], [0, -1], [-1, 1]];
const RING2: [number, number][] = [[2, -1], [1, 1], [2, 0], [-1, 2], [0, 2], [-2, 1], [-2, 2], [2, -2], [1, -2], [-2, 0], [0, -2], [-1, -1]];

/**
 * Where the winch stands, in sim units: the centre of the first free cell round the forge (no building, no work, ground
 * you could build on, not the armory rack's cell), else round the keep. `buildable` says whether a cell is open ground.
 */
export const winchSpot = (s: CampaignState, forgeKey: string | null, rackCell: string | null, buildable: (q: number, r: number) => boolean): { x: number; y: number; cell: string } | null => {
  const free = (q: number, r: number): boolean => {
    const k = `${q},${r}`;
    return k !== forgeKey && k !== rackCell && !(q === 0 && r === 0) && !s.cells[k]?.built && !s.cells[k]?.job && buildable(q, r);
  };
  const around = (c: { q: number; r: number }, list: [number, number][]): { x: number; y: number; cell: string } | null => {
    for (const [dq, dr] of list) {
      const q = c.q + dq;
      const r = c.r + dr;
      if (free(q, r)) return { ...hexToWorld(q, r), cell: `${q},${r}` };
    }
    return null;
  };
  if (forgeKey) {
    const f = parseHexKey(forgeKey);
    const at = around(f, RING) ?? around(f, RING2);
    if (at) return at;
  }
  // No forge standing (or no room by it): beside the keep, a little out.
  return around({ q: 0, r: 0 }, RING2);
};

/** The cell a sim point falls in (the armory rack's spot). */
export const cellOf = (p: { x: number; y: number }): string => {
  const hx = worldToHex(p.x, p.y);
  return `${hx.q},${hx.r}`;
};

/** The winch as the card draws it: the shaft lit red, the frame and the rope, the stones won so far. */
const TOKEN: Record<StratumId, string> = { ossuary: '#6b7480', marrow: '#ff6a3a', glass: '#1a161e', cisterns: '#2f6c6a', buried: '#6a4a34', grotto: '#5ff2ff', frost: '#cfeaff', geode: '#9a62f0', archive: '#c4c7cc', forge: '#2a2a2e', nest: '#e4e7ec', ribs: '#9c9c98' };
const winchArt = (strata: StratumId[], stage: number, up: boolean): string => {
  const hang = strata.slice(0, 12).map((st, i) => {
    const y = 34 + i * 4.6;
    const x = i % 2 ? 54 : 66;
    return `<path d="M60 ${y - 2}L${x} ${y}" stroke="#8a8780" stroke-width="1"/><circle cx="${x}" cy="${y + 2.4}" r="2.6" fill="${TOKEN[st]}" stroke="#0d0f13" stroke-width="0.6"/>`;
  }).join('');
  return `<svg viewBox="0 0 120 120" aria-hidden="true">
<defs><radialGradient id="wsg" cx="50%" cy="84%" r="50%"><stop offset="0" stop-color="#ff4f3a" stop-opacity="${up ? (stage >= 3 ? 0.55 : 0.38) : 0.08}"/><stop offset="1" stop-color="#ff4f3a" stop-opacity="0"/></radialGradient></defs>
<rect width="120" height="120" rx="10" fill="#0d0f13"/><circle cx="60" cy="96" r="54" fill="url(#wsg)"/>
<g opacity="${up ? 1 : 0.35}">
${stage >= 2 ? '<g><path d="M14 18v74M106 18v74" stroke="#3a2a20" stroke-width="2.5"/><path d="M16 22h18v34l-9-6-9 6zM104 22H86v34l9-6 9 6z" fill="#24272e"/><path d="M19 32h12M89 32h12" stroke="#c0392b" stroke-width="3"/></g>' : ''}
<path d="M30 96L42 22M54 96L42 22M66 96L78 22M90 96L78 22" stroke="#4a372b" stroke-width="4" stroke-linecap="round"/>
<rect x="34" y="18" width="52" height="6" rx="1" fill="#45342a"/>
<circle cx="60" cy="29" r="5" fill="none" stroke="#5a5e66" stroke-width="2.4"/>
${stage >= 1 ? '<circle cx="46" cy="29" r="4" fill="none" stroke="#5a5e66" stroke-width="2"/><path d="M46 33v20" stroke="#8a8780" stroke-width="1.2"/><path d="M40 53h12l-2 9h-8z" fill="#3a3d44"/>' : ''}
<path d="M60 34v58" stroke="#8a8780" stroke-width="1.6"/>
${hang}
<ellipse cx="60" cy="96" rx="34" ry="9" fill="#3a3c43"/><ellipse cx="60" cy="95" rx="27" ry="6" fill="#050405" stroke="#ff4f3a" stroke-width="1.6"/>
${stage >= 3 ? '<path d="M56 86c-4-6 4-8 0-14M64 84c4-6-4-9 0-15" stroke="#4a4448" stroke-width="2.4" fill="none" stroke-linecap="round" opacity="0.8"/>' : ''}
</g></svg>`;
};

/**
 * The winch's card: the mark ever and this week's, when the Chasm remakes itself, who went down each time the mark went
 * deeper, and what it still can grow. `day`: the campaign day (for the week).
 */
export const winchTrophyBox = (s: CampaignState, day: number, compact = false): HTMLElement => {
  const w = simaWinch(s);
  const rec = simaRecord(s, day);
  const mark = rec.best;
  const left = SIMA.weekDays - (day % SIMA.weekDays);
  const name = (pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '';
  const stones = rec.stones.length;
  const steps: [boolean, string, string][] = [
    [stones > 0, t('sima.winch.stones', { n: stones, max: STRATA.length }), t('sima.winch.stonesHow', { every: SIMA.threshold })],
    [mark >= SIMA_TROPHY.pulley, t('sima.winch.pulley'), t('sima.winch.reach', { n: SIMA_TROPHY.pulley })],
    [mark >= SIMA_TROPHY.banners, t('sima.winch.banners'), t('sima.winch.reach', { n: SIMA_TROPHY.banners })],
    [mark >= SIMA_TROPHY.smoke, t('sima.winch.smoke'), t('sima.winch.reach', { n: SIMA_TROPHY.smoke })],
  ];
  // Each time the mark went deeper, the floor and who went: the deepest first, a few.
  const marks = [...rec.marks].sort((a, b) => b.floor - a.floor).slice(0, 5);
  const body = h(
    'div.trophy-body',
    {},
    h('span.label', {}, t('sima.winch.label')),
    h('h4', {}, t('sima.winch.title')),
    w
      ? h(
          'p.small',
          { 'data-test': 'winch-marks' },
          mark > 0 ? t('sima.winch.mark', { n: mark }) : t('sima.winch.noMark'),
          ' · ',
          rec.weekBest > 0 ? t('sima.winch.week', { n: rec.weekBest }) : t('sima.winch.weekNone'),
          ' · ',
          left === 1 ? t('sima.winch.remakeOne') : t('sima.winch.remake', { d: left }),
        )
      : h('p.small', {}, t('sima.winch.notYet')),
  );
  if (marks.length) {
    body.append(
      h('h5.winch-who', {}, t('sima.winch.who')),
      h(
        'ul.winch-marks',
        {},
        ...marks.map((m) => {
          const names = m.pids.map(name).filter(Boolean);
          return h('li', {}, h('b', {}, t('sima.winch.floor', { n: m.floor })), h('span.small', {}, names.length ? names.join(', ') : '—'));
        }),
      ),
    );
  }
  body.append(h('ul.trophy-steps', {}, ...steps.map(([ok, what, how]) => h(`li${ok ? '.ok' : ''}`, {}, h('span.trophy-mark', { html: ok ? ICONS.check : ICONS.lock }), h('span', {}, h('b', {}, what), h('span.small', {}, how))))));
  return h(`section.trophy${compact ? '.compact' : ''}${w ? '.won' : ''}`, { 'data-test': 'winch-trophy' }, h('div.trophy-art', { html: winchArt(rec.stones.map((x) => x.stratum), w?.stage ?? 0, !!w) }), body);
};
